// Shared types for the data layer. Nothing here talks to the network.

export type Role = 'owner' | 'editor' | 'viewer';

export interface Session {
  userId: string;
  email: string;
  displayName: string;
}

export interface Membership {
  familyId: string;
  familyName: string;
  role: Role;
  allPatients: boolean;
}

export interface Member {
  userId: string;
  email: string;
  displayName: string;
  role: Role;
  allPatients: boolean;
  patientIds: string[];
  joinedAt: string;
}

export interface Invite {
  id: string;
  email: string;
  role: Exclude<Role, 'owner'>;
  allPatients: boolean;
  patientIds: string[];
  token: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
}

export interface InvitePeek {
  familyName: string;
  invitedEmail: string;
  role: Role;
  invitedByName: string;
  status: 'open' | 'accepted' | 'expired' | 'revoked';
}

/** temp_password = activated with a one-time password the user hasn't replaced yet. */
export type UserStatus = 'active' | 'invited' | 'deactivated' | 'temp_password';

/** After sign-in: 'change' = signed in with a one-time password, must choose their own. */
export type PasswordStatus = 'none' | 'change' | 'expired';

export interface AdminUser {
  userId: string;
  email: string;
  displayName: string;
  createdAt: string;
  lastSignInAt: string | null;
  status: UserStatus;
  inviteExpiresAt: string | null;
  tempExpiresAt: string | null;
  isAdmin: boolean;
  families: string;
}

/** A family member as the super admin sees it (any family). */
export interface AdminMember {
  userId: string;
  email: string;
  displayName: string;
  role: Role;
  allPatients: boolean;
  patientIds: string[];
  joinedAt: string;
  /** Phone-login user name, if this is a phone login. */
  username: string | null;
}

export interface AdminFamily {
  familyId: string;
  name: string;
  ownerEmail: string;
  patients: number;
  members: number;
}

export type AdminUserAction =
  | { action: 'create'; email: string; name: string; familyId?: string; role?: 'editor' | 'viewer' }
  | { action: 'reinvite'; userId: string }
  | { action: 'activate'; userId: string }
  | { action: 'reset_password'; userId: string }
  | { action: 'deactivate'; userId: string }
  | { action: 'delete'; userId: string };

/** token = set-password link; tempPassword = one-time password (both expire at expiresAt). */
export interface AdminActionResult { userId?: string; token?: string; tempPassword?: string; expiresAt?: string; warning?: string }

export interface UserInvitePeek { email: string; name: string; status: 'open' | 'used' | 'expired' }

/** Tables that hold app data (one row per record). */
export type DataTable =
  | 'patients'
  | 'medicines' | 'library_items' | 'reminders' | 'visits' | 'doctors' | 'tests' | 'vaccinations'
  | 'medicine_types' | 'test_library'
  | 'family_settings'
  | 'user_settings';

/** Key/value tables: `id` is the setting key and `data` its string value. */
export const SETTING_TABLES = ['family_settings', 'user_settings'] as const;
export const isSettingTable = (t: DataTable) => t === 'family_settings' || t === 'user_settings';

export const PATIENT_TABLES = ['medicines', 'library_items', 'reminders', 'visits', 'doctors', 'tests', 'vaccinations'] as const;
export type PatientTable = typeof PATIENT_TABLES[number];
export const FAMILY_LIST_TABLES = ['medicine_types', 'test_library'] as const;

/**
 * A data row as the store sees it. `patientId` is set for per-patient tables.
 * For family_settings / user_settings, `id` is the key and `data` is the string value.
 */
export interface DataRow {
  table: DataTable;
  patientId?: string;
  id: string;
  position: number;
  data: unknown;
}

export interface FamilySnapshot {
  rows: DataRow[];
  fetchedAt: number;
}

export interface Change {
  table: DataTable;
  patientId?: string;
  upserts: { id: string; position: number; data: unknown }[];
  deletes: string[];
}

export type RemoteEvent =
  | { kind: 'row'; type: 'upsert'; row: DataRow }
  | { kind: 'row'; type: 'delete'; table: DataTable; patientId?: string; id: string }
  | { kind: 'membership' };

/** Thrown by a backend when the server refused a change (no permission, bad reference). */
export class RejectedError extends Error {
  constructor(message: string, public code?: string) { super(message); }
}
